import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Box,
  CircularProgress,
  makeStyles,
  createStyles,
  Theme,
} from '@material-ui/core';
import { Link } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import ComponentIcon from '@material-ui/icons/Extension';
import TemplateIcon from '@material-ui/icons/FileCopy';
import ApiIcon from '@material-ui/icons/Http';
import { Entity } from '@backstage/catalog-model';
import { useUserProfile } from './useUserProfile';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    cardHeader: {
      padding: theme.spacing(2, 2, 1, 2),
    },
    content: {
      flex: 1,
      overflow: 'auto',
      padding: 0,
    },
    listItem: {
      borderBottom: `1px solid ${theme.palette.divider}`,
      '&:last-child': { borderBottom: 'none' },
    },
    chip: {
      marginRight: theme.spacing(0.5),
      height: 20,
      fontSize: '0.65rem',
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(4),
      color: theme.palette.text.secondary,
    },
    loading: {
      display: 'flex',
      justifyContent: 'center',
      padding: theme.spacing(4),
    },
  }),
);

const getEntityIcon = (kind: string) => {
  switch (kind.toLowerCase()) {
    case 'component':
      return <ComponentIcon />;
    case 'template':
      return <TemplateIcon />;
    case 'api':
      return <ApiIcon />;
    default:
      return <ComponentIcon />;
  }
};

interface RecommendationsWidgetProps {
  maxItems?: number;
}

export const RecommendationsWidget = ({
  maxItems,
}: RecommendationsWidgetProps) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const { profile } = useUserProfile();
  const [recommendations, setRecommendations] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use itemsPerWidget from preferences, or maxItems prop, or default to 5
  const itemsToShow =
    maxItems ?? profile.dashboardPreferences?.itemsPerWidget ?? 5;

  useEffect(() => {
    const fetchRecommendations = async () => {
      setLoading(true);
      setError(null);
      try {
        // Build filters based on user profile
        const filters: Record<string, string>[] = [];

        if (profile.interests?.includes('apis')) {
          filters.push({ kind: 'API' });
        }
        if (profile.interests?.includes('microservices')) {
          filters.push({ kind: 'Component', 'spec.type': 'service' });
        }

        // Default: get recent components and templates
        if (filters.length === 0) {
          filters.push({ kind: 'Component' });
          filters.push({ kind: 'Template' });
        }

        const { items } = await catalogApi.getEntities({
          filter: filters,
          limit: itemsToShow,
        });

        // Sort by metadata.annotations['backstage.io/created-at'] if available
        // Use slice() to avoid mutating the original array
        const sorted = items.slice().sort((a, b) => {
          const aDate =
            a.metadata.annotations?.['backstage.io/created-at'] || '';
          const bDate =
            b.metadata.annotations?.['backstage.io/created-at'] || '';
          return bDate.localeCompare(aDate);
        });

        setRecommendations(sorted.slice(0, itemsToShow));
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load recommendations';
        setError(errorMessage);
        setRecommendations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendations();
  }, [catalogApi, profile.interests, itemsToShow]);

  const getEntityLink = (entity: Entity) => {
    const kind = entity.kind.toLowerCase();
    const namespace = entity.metadata.namespace || 'default';
    const name = entity.metadata.name;
    return `/catalog/${namespace}/${kind}/${name}`;
  };

  return (
    <Card className={classes.card}>
      <CardHeader
        className={classes.cardHeader}
        title="Recommended for You"
      />
      <Divider />
      <CardContent className={classes.content}>
        {loading ? (
          <Box className={classes.loading}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Box className={classes.emptyState}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        ) : recommendations.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="body2">
              No recommendations yet. Complete your profile to get personalized
              suggestions.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {recommendations.map(entity => (
              <ListItem
                key={`${entity.kind}-${entity.metadata.namespace}-${entity.metadata.name}`}
                className={classes.listItem}
                button
                component={Link}
                to={getEntityLink(entity)}
              >
                <ListItemIcon>{getEntityIcon(entity.kind)}</ListItemIcon>
                <ListItemText
                  primary={entity.metadata.title || entity.metadata.name}
                  secondary={
                    <Box>
                      <Chip
                        label={entity.kind}
                        size="small"
                        className={classes.chip}
                        variant="outlined"
                      />
                      {entity.spec?.type && (
                        <Chip
                          label={String(entity.spec.type)}
                          size="small"
                          className={classes.chip}
                          variant="outlined"
                        />
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default RecommendationsWidget;
